import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../axios/axiosInstance';
import { useEffect, useRef, useState } from 'react';
import { Input, message, Popconfirm, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { useParams } from 'react-router-dom';
import { ModePage } from 'common/enum';
import AddNewGameModal from './AddNewGameModal';

import processError from '../../../../utils/processError';
import { Game } from '../../../../model/response';
import { GameStatus, GameType } from '../../../../model/enum';
import { gameStatusList, gameTypeList } from '../../../../utils/enumMapper';
import useTableConfig from '../../../../hooks/useTableConfig';

export default function GameManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig();
  const params = useParams();
  const [keyword, setKeyword] = useState<string>('');
  const [gameId, setGameId] = useState<string>('');
  const [modeEdit, setModeEdit] = useState<ModePage>(ModePage.ADD);
  const rootElement: any = useRef(null);
  const [isOpen, setOpenModal] = useState<boolean>(false);

  const getGameTypeLabel = (value: GameType): string => {
    const type = gameTypeList.find((type) => type.value === value);
    return type ? type.label : '';
  };

  const getGameStatusLabel = (value: GameStatus): string => {
    const status = gameStatusList.find((status) => status.value === value);
    return status ? status.label : '';
  };

  const [data, setData] = useState<Game[]>();
  const [loading, setLoading] = useState(false);

  const columns: ColumnsType<Game> = [
    {
      title: 'Tên',
      dataIndex: 'name',
      width: '30%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('name'),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      render: (_, record) => getGameTypeLabel(record.type),
      width: '15%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('type'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      render: (_, record) => getGameStatusLabel(record.status),
      width: '15%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('status'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      width: '25%',
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined />
          </button>
          <Popconfirm
            title="Xóa game"
            description="Xác nhận xóa game. Hành động này không thể hoàn tác?"
            onConfirm={() => deleteGame(record)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const deleteGame = async (record: Game) => {
    setLoading(true);

    try {
      await axiosInstance({
        url: `/games/${record.id}`,
        method: 'delete',
      });
      message.success('Deleted successfully.');
      await loadListTheories();
    } catch (e) {
      processError(e);
    }
    setLoading(false);
  };

  const moveEditView = async (e: any, record: Game) => {
    setModeEdit(ModePage.EDIT);
    setOpenModal(true);
    setGameId(record.id);
  };

  const loadListTheories = async () => {
    setLoading(true);
    const res = await axiosInstance({
      url: '/games',
      params: {
        keyword,
        size: pageSize,
        page: currentPage,
        sortBy,
        direction,
      },
    });

    if (res.status === 200) {
      setData(res.data);
      setLoading(false);
      setTotalElements(res.data.totalElements);
    }
  };

  const onSearch = (e: any) => {
    setKeyword(e.target.value);
  };

  const addNewGame = () => {
    setModeEdit(ModePage.ADD);
    setOpenModal(true);
  };

  const onCloseModal = () => {
    setOpenModal(false);
  };

  const onSave = () => {
    setOpenModal(false);
    loadListTheories().then();
  };

  useEffect(() => {
    loadListTheories().then();
  }, [currentPage, pageSize, sortBy, direction, params.subjectId, keyword]);

  return (
    <div className="w-full h-full p-4" ref={rootElement}>
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2" onClick={addNewGame}>
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
          <div className="min-w-[170px] max-w-[170px] flex items-center gap-2"></div>
        </div>
        <Input placeholder="Search..." name="keyword" className="w-fit" onPressEnter={onSearch} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          scroll={{ y: (window.innerHeight * 52) / 100 }}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <AddNewGameModal
        isOpen={isOpen}
        onCloseModal={onCloseModal}
        modePage={modeEdit}
        onSave={onSave}
        gameId={gameId}
      />
    </div>
  );
}
